<template>
    <div v-bind:class="{ 'min-h-screen' : view === 'split'}" class="flex flex-wrap w-full relative" id="listings-container">
        <!-- Listing Cards
            Visible on List and Split view, hidden on Map view
        -->
        <ul
            class="listings listings--map flex flex-wrap items-start -mx-5"
            :class="[view == 'list' ? 'w-full -mt-30' : 'w-1/2 overflow-scroll -mt-10 fade']"
            v-show="view != 'map'"
        >
            <!-- :class="{'listings-vertical' : orientation == 'vertical' }" -->
            <Listing
                v-for="(listing, index) in listings"
                :key="listing.mlsId+'-'+index"
                :id="'listing-'+listing.mlsId+'-'+index"
                :listing="listing"
                :index="index"
                :orientation="orientation"
                :split-active="view != 'split' || index < splitVisibleCount"
                :parentAgentId="parentAgentId"
                :agentRepresenting="agentRepresenting"
                :showRepresentationLabel="showRepresentationLabel"
            />

            <li v-show="view == 'split'" class="w-full relative z-max px-40">
                <div v-if="this.loading" class="text-gray200 text-xl font-bold py-50 flex items-center justify-center">
                    Loading, please wait...
                </div>

                <div
                    v-if="!this.loading && count === 0"
                    class="text-gray200 text-xl font-bold py-50 flex items-center justify-center"
                >
                    No listings found, please try another search.
                </div>

                <!-- class="w-full sm:w-1/2 lg:w-1/3 2xl:w-1/4" -->
                <div v-if="allLoaded && count > 0 && count < parseInt(countTotal.toString().replaceAll(/,/g, ''))" class="text-gray200 text-xl font-bold py-50 text-center">
                    Showing {{count}} of {{countTotal}} results. Zoom or refine your search to view more.
                </div>
                <div v-else-if="allLoaded" class="text-gray200 text-xl font-bold py-50 flex items-center justify-center">
                    There are no more listings.
                </div>

                <div v-show="count > limit && page % 3 == 0 && !loading && !allLoaded" class="w-full">
                    <div class="w-full flex justify-center items-center py-80">
                        <a v-if="view == 'split'" class="filled-button filled-button-red block cursor-pointer" @click="$emit('get-listings-split')">
                            <span class="filled-button-content">Load More</span>
                        </a>
                    </div>
                </div>
            </li>
        </ul>

        <!-- Map -->
        <div
            v-show="view != 'list'"
            class="mapContainerClass"
            :class="view"
            id="map-container"
        >

            <!-- No Results message -->
            <div
                v-show="count === 0 && !this.loading"
                class="bg-blue400 p-30 rounded trans text-center text-white text-sm"
            >
                No results in this area, try broadening your search using the filters above.
            </div>

            <!-- Entire map apparatus -->
            <div class="map-wrapper h-full bg-blue200 w-full relative">

                <!-- Loading message -->
                <div
                    v-if="this.loading"
                    class="map-notification-wrapper p-20 w-full absolute flex justify-center"
                >
                    Loading, please wait...
                </div>

                <div
                    v-if="mapUnavailable"
                    class="absolute pin flex justify-center items-center z-3 bg-blue200"
                >
                    <div class="bg-blue400 p-30 rounded trans text-center text-white text-sm">
                        Map view is unavailable right now. Please use the listing results or try again later.
                    </div>
                </div>


                <!-- Results count message -->
                <div
                    v-else-if="!this.loading && count > 0 && count < parseInt(countTotal.toString().replaceAll(/,/g, ''))"
                    class="map-notification-wrapper p-20 w-full absolute flex justify-center"
                >
                    <div class="map-notification px-20 py-15 trans rounded text-white text-sm">
                        Showing {{count}} of {{countTotal}} results. Zoom or refine your search to view more.
                    </div>
                </div>

                <!-- Actual map itself -->
                <div id="map" class="w-full h-full" style="font-family: inherit;"></div>

                <!-- Zoom Controls -->
                <div v-if="map" class="absolute pin-t pin-r m-15 bg-white rounded cursor-pointer">
                    <div class="">
                        <div @click="setZoom(1)" v-show="showMaxZoom" class="px-12 py-5 h-40 flex items-center justify-center border-b-2">
                            <img src="/assets/img/plus.svg" alt="Zoom In"/>
                        </div>
                        <div class="w-full h-1 bg-gray100"></div>
                        <div @click="setZoom(-1)" v-show="showMinZoom" class="px-12 py-5 h-40 flex items-center justify-center">
                            <img src="/assets/img/minus.svg" alt="Zoom Out"/>
                        </div>
                    </div>
                </div>

                <!-- Sat / Map -->
                <div
                    v-if="map && mapStyle == 'streets-v9'"
                    class="absolute pin-t pin-l m-15 p-10 px-20 bg-white rounded
                    uppercase tracking-looser text-tiny font-secondary font-bold cursor-pointer"
                    @click="setMapStyle('satellite-v9')"
                >
                    Satellite
                </div>
                <div
                    v-else-if="map"
                    class="absolute pin-t pin-l m-15 p-10 px-20 bg-white rounded
                    uppercase tracking-looser text-tiny font-secondary font-bold cursor-pointer"
                    @click="setMapStyle('streets-v9')"
                >
                    Map
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Listing from './listing.vue';
import addCommas from '../../helpers/addCommas';

// Renders residential cards and maintains list, split, and Mapbox views.
export default {
    props: {
        listings: { type: Array, default: () => [] },
        total: { type: [Number, String], default: 0 },
        loading: { type: Boolean, default: true },
        page: {
            default: 1
        },
        limit: {
            default: 21
        },
        orientation: {
            default: 'horizontal'
        },
        view: {
            default: 'split'
        },
        query: {
            default: false
        },
        searchStringUpdated: {
            default: false
        },
        allLoaded: {
            default: false
        },
        splitVisibleCount: {
            default: 0
        },
        parentAgentId: { type: [String, Number], default: null },
        agentRepresenting: { type: String, default: '' },
        showRepresentationLabel: { type: Boolean, default: false }
    },
    emits: ['update-map', 'get-listings-split'],
    data() {
        return {
            map: null,
            mapUnavailable: false,
            mapStyle: 'streets-v9',
            mapOffsetTop: null,
            windowScrollTop: null,
            navHeight: 120,
            height: null,
            width: null,
            minZoom: 6,
            maxZoom: 18,
            showMaxZoom: true,
            showMinZoom: true,
            currentZoom: 10,
            zoomReloading: false,
            pendingCenterUpdate: false,
            count: 0,
            countTotal: 0,
        }
    },
    mounted () {
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('resize', this.handleResize);
        this.handleScroll()
        this.handleResize()

        //initialize map
        if (this.view != 'list') {
            this.createMap();
        }
    },
    unmounted () {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);

        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    },
    beforeUpdate(){
        var vm = this;

         if(vm.map){
            //check zoom just in case... Double click
            vm.checkZoom();
        }

        //start clean: remove markers
        vm.removeListings();
    },
    updated() {
        //Any change will call this method: Example new listings getting added
        var vm = this

        //add markers
        if (this.view != 'list' && vm.listings.length) {
            vm.countTotal = vm.total;
            vm.addListings()
        }
        else{
            vm.count = 0;
        }
    },
    watch: {
        view() {
            var vm = this
            if (this.view == 'list') {
                return;
            }

            vm.$nextTick(function() {
                if (!vm.map) {
                    vm.createMap();
                }

                vm.refreshMap({
                    recenter: vm.pendingCenterUpdate
                });
            });

        },
        searchStringUpdated(){
            var vm = this;

            if(!this.searchStringUpdated){
                return;
            }

            if (this.view == 'list') {
                this.pendingCenterUpdate = true;
                return;
            }

            vm.refreshMap({
                recenter: true
            });
        },
        map() {
            var vm = this
            if (this.map) {
                this.map.on('moveend', function (e) {
                    //don't fire this off if it is a zoom based event. This will cause more unnecessary reloads.
                    if(!vm.zoomReloading && vm.view != 'list')
                        vm.$emit('update-map', vm.map.getBounds())
                });
            }
        }
    },
    components: {
        Listing
    },
    computed: {
        mapContainerClass() {
            var mapContainerClass = ''

            // If the view is map
            if (this.view == 'map') {
                mapContainerClass += 'w-full '
            // If it's split view (or list)
            } else {
                mapContainerClass += 'w-1/2 pl-10 ml-10 '
            }

            if (this.view == 'split') {
                //this.updateMapPinning();
            }

            return mapContainerClass
        }
    },
    methods: {
        setMapStyle(style) {
            if (!this.map) {
                return;
            }

            this.mapStyle = style
            this.map.setStyle('mapbox://styles/mapbox/' + style)
        },
        handleScroll() {
            this.mapOffsetTop = document.getElementById("listings-container").offsetTop
            this.windowScrollTop = document.documentElement.scrollTop
        },
        handleResize() {
            // Define the height
            let height = document.documentElement.clientHeight;
            // Define the width
            let width = document.documentElement.clientWidth
            // If the height has changed, refacror the map
            if (height != this.height || width != this.width) {
                this.height = height
                this.width = width

                if(this.map && this.view != 'list')
                    this.map.resize();
            }
            // define nav height by width
            if (width <= 1345) {
                this.navHeight = 120
            } else {
                this.navHeight = 131
            }
        },
        refreshMap({ recenter = false } = {}) {
            var vm = this;

            if (!vm.map || vm.view == 'list') {
                return;
            }

            vm.$nextTick(function() {
                window.requestAnimationFrame(function() {
                    if (!vm.map || vm.view == 'list') {
                        return;
                    }

                    vm.map.resize();
                    vm.checkZoom();

                    if (recenter) {
                        vm.pendingCenterUpdate = false;
                        vm.setCenter();
                    }
                });
            });
        },
        propertyLabel(listing) {

            var propertyLabel = ''

            let type = listing.property.type
            let subType = String(listing.property.subType).toLowerCase()
            let status = String(listing.mls.status).toLowerCase()

            // Determine base label
            if (subType == 'townhouse') {
                propertyLabel += 'Townhouse'
            } else if (subType == 'condominium') {
                propertyLabel += 'Condo'
            } else if (subType == 'apartment') {
                propertyLabel += 'Apartment'
            } else if (type == 'RES') {
                propertyLabel += 'Single Family'
            } else if (type == 'MLF') {
                propertyLabel += 'Multi-Family'
            } else if (type == 'RNT') {
                propertyLabel += 'Condo'
            } else if (type == 'LND') {
                propertyLabel += 'Land'
            }

            // Add Status if necessary
            if (status == 'pending') {
                propertyLabel += ' • Pending'
            } else if (status == 'activeundercontract') {
                propertyLabel += ' • Under Contract'
            }
            else if (status == 'closed') {
                propertyLabel += ' • Closed'
            }
            else if (status == 'comingsoon') {
                propertyLabel += ' • Coming Soon'
            }

            return propertyLabel
        },
        iconClasses(listing) {
            var types = {
                'townhouse': {
                    defaultColor: 'text-red500',
                    icon: 'townhouse',
                },
                'apartment': {
                    defaultColor: 'text-red500',
                    icon: 'townhouse',
                },
                'condominium': {
                    defaultColor: 'text-purple500',
                    icon: 'condo',
                },
                'RES': {
                    defaultColor: 'text-red500',
                    icon: 'single-family',
                },
                'RNT': {
                    defaultColor: 'text-red500',
                    icon: 'single-family',
                },
                'MLF': {
                    defaultColor: 'text-blue500',
                    icon: 'multifamily',
                },
                'CRE': {
                    defaultColor: 'text-gray500',
                    icon: 'commercial',
                },
                'LND': {
                    defaultColor: 'text-green500',
                    icon: 'land',
                },
            }

            var iconClasses = ''
            var typeAbbreviation = listing.property.type
            var subType = String(listing.property.subType).toLowerCase()

            var type = types[subType] || types[typeAbbreviation]

            iconClasses += type.defaultColor + ' '

            // Define the size and icon
            iconClasses += 'icon-m-' + type.icon

            return iconClasses
        },
        removeListings() {
            $(".map-marker").remove()
        },
        addListings() {
            var vm = this

            if (!window.mapboxgl) {
                vm.mapUnavailable = true;
                return { type: "Locations", features: [] };
            }

            if (!vm.map) {
                return { type: "Locations", features: [] };
            }

            var features = []
            for (const listing of vm.listings) {
                if(listing.geo){
                    features.push(
                        {
                            geometry: {
                                type: "Point",
                                coordinates: [listing.geo.lng, listing.geo.lat]
                            },
                            listing: listing,
                        },
                    )
                }
            }

            var locations = {
              "type": "Locations",
              "features": features
            };

            this.count = locations.features.length

            locations.features.forEach(function(marker) {
                // create a DOM element for the marker
                var el = document.createElement('div');
                // el.className = 'map-marker icon-s-single-family';
                var listing = marker.listing
                var listingURL = `/listings/${listing.listingId}`
                var details = ''

                if (listing.property.bedrooms) {
                    details += `
                    <div class="flex px-10" v-if="beds">
                        <span class="icon-m-bed text-gray300"></span>
                        <div class="listing-info-item ml-10">${listing.property.bedrooms}</div>
                    </div>`
                }

                if (listing.property.bathrooms) {
                    details += `
                    <div class="flex px-10" v-if="baths">
                        <span class="icon-m-tub text-gray300"></span>
                        <div class="listing-info-item ml-10">${listing.property.bathrooms}</div>
                    </div>`
                }

                if (listing.property.area) {
                    details += `
                    <div class="flex px-10" v-if="area">
                        <span class="icon-m-measure text-gray300"></span>
                        <div class="listing-info-item ml-10">${ addCommas(listing.property.area) }</div>
                    </div>`
                }

                if (listing.property.type == 'LND' && listing.property.acres) {
                    details += `
                    <div class="flex px-10 acres" v-if="listing.property.type == 'LND' && acres">
                        <span class="icon-m-tree text-gray300"></span>
                        <div class="listing-info-item ml-10">${ listing.property.acres }</div>
                    </div>`
                }

                if (listing) {
                    if (listing.property.type == "MLF") {
                        el.className = 'map-marker blue icon-s-multifamily';
                    } else if (listing.property.type == "CRE") {
                        el.className = 'map-marker blue icon-s-commercial';
                    } else if (listing.property.type == "LND") {
                        el.className = 'map-marker green icon-s-land';
                    } else if(listing.property.subType == "Condominium"){
                        el.className = 'map-marker purple icon-s-condo';
          }else if(listing.property.subType == "Townhouse"){
            el.className = 'map-marker gray icon-s-townhouse';
                    }else {
                        el.className = 'map-marker icon-s-single-family';
                    }
                } else {
                    el.className = 'map-marker icon-s-single-family';
                }

                // var propertyLabel = vm.propertyLabel(listing)

                // add marker to map
                if(vm.map){
                    new window.mapboxgl.Marker(el)
                        .setLngLat(marker.geometry.coordinates)
                        .setPopup(new window.mapboxgl.Popup({ offset: 0 }) // add popups
                            .setHTML(`
                            <div class="listing-item" style="width: 300px;">
                                <a 	class="listing-item-header cursor-pointer" href="${listingURL}" style="z-index: initial;">
                                    <span
                                        class="bg-blue200 cursor-pointer absolute pin bg-cover bg-center" style="background-image: url(${listing.photos[0]}); z-index: -1;"
                                    ></span>
                                </a>
                                <div class="listing-item-details-wrapper" style="padding: 0px">
                                    <a 	class="listing-item-details cursor-pointer" style="margin-top: 0px; border-radius: 0px;" href="${listingURL}">
                                        <div class="listing-item-type" style="top: 0px; left: 30px;">
                                            <div class="absolute pin opacity-40 rounded-b ${ vm.iconClasses(listing).replace('text-', 'bg-').replace('icon-m', '') }"></div>
                                            <div class="relative">
                                                ${ vm.propertyLabel(listing) }
                                            </div>
                                        </div>

                                        <div class="listing-item-details-top" style="padding: 25px; margin-top: 0px">
                                            <div class="flex items-center ${listing.property.type}">
                                                <span class="${ vm.iconClasses(listing) } -ml-5 listing-item-icon"></span>
                                                <div class="text-xl text-gray700 font-secondary font-bold">$${ addCommas(listing.listPrice) }</div>
                                            </div>

                                            <div class="mt-20">
                                                <div class="font-bold text-gray700">${listing.address.full}</div>
                                                <div class="mt-10 text-sm">${listing.address.city}, ${listing.address.state} ${listing.address.postalCode}</div>
                                            </div>

                                        </div>
                                        <div class="border-t border-solid border-gray100">
                                            <div class="-mx-10 p-25 flex justify-between">
                                                ${details}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            </div>
                            `)
                        )
                        .setOffset([0, -18])
                        .addTo(vm.map);
                }
            });

            return locations
        },
        createMap() {
            var vm = this

            if (vm.map) {
                return vm.map;
            }

            if (!window.mapboxgl) {
                vm.mapUnavailable = true;
                return null;
            }

            window.mapboxgl.accessToken = 'pk.eyJ1Ijoic3RlYWRmYXN0ZGVzaWduZmlybSIsImEiOiJjanA4cjJua3gyMms4M3FwN2h2dGtpY2FsIn0.WNrUU--j6DaF_aV6PiTrcA';

            try {
                vm.map = new window.mapboxgl.Map({
                    container: 'map',
                    style: 'mapbox://styles/mapbox/' + this.mapStyle,
                    scrollZoom: false,
                    zoomControl: false,
                    minZoom: this.minZoom,
                    maxZoom: this.maxZoom,
                    zoom: this.currentZoom,
                    center: {lat: 35.945220, lng: -78.845510}
                });
            } catch (error) {
                console.warn('Mapbox failed to initialize.', error);
                vm.mapUnavailable = true;
                return null;
            }

            vm.mapUnavailable = false;


            //inform listings-container.vue that our map is ready
            // -- one downfall is that this won't show until all 500 are loaded.
            // -- On the other hand you would see 21 and then a reload of the remaining 500. Not sure what looks best  ¯\_(ツ)_/¯
            vm.$emit('update-map', vm.map.getBounds())

            return vm.map;
        },
        setCenter(){
            var vm = this;

            if(vm.map && vm.view != 'list'){
                let points = [];


                //prep and set center point so marker is within view
                for (var listing of vm.listings) {
                    if(listing.geo && listing.geo.lng && listing.geo.lat){
                        points.push([listing.geo.lng, listing.geo.lat]);
                    }
                }

                var coordinates = points;
                if (!coordinates.length) {
                    return;
                }

                var bounds = coordinates.reduce(function(bounds, coord) {
                    return bounds.extend(coord);
                }, new window.mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));


                if(bounds && bounds['_sw'] && bounds['_ne']){
                    vm.zoomReloading = true;

                    //little ugly but set a temp maxZoom for the bounds (so it doesn't over zoom on address) and then reapply OG settings
                    vm.map.setMaxZoom(15)
                    vm.map.fitBounds(bounds, {padding: 100}); //fit all markers within view

                    //update settings
                    vm.map.once('zoomend', function() {
                        //give this some time to stop animation
                        setTimeout(function() {
                            if (!vm.map) {
                                return;
                            }

                            vm.zoomReloading = false;
                            vm.map.setMaxZoom(vm.maxZoom)

                            //we need to update the bounds but we don't want to reload all of the listings again
                            var tempArr = {
                                hold: true, //we check for this value in listings-container.vue to tell us not to run getListings()
                                bounds: vm.map.getBounds() //bounds - normally this is what gets passed alone
                            }
                            //notify listings-container.vue with our new bounds
                            vm.$emit('update-map', tempArr)

                        }, 500);
                    });
                }
            }
        },

        setZoom(zoom){
            var zoom = this.map.getZoom() + zoom;

            //zoom to new setting
            this.map.zoomTo(zoom);

            this.checkZoom()
        },
        checkZoom(){
            this.currentZoom = this.map.getZoom();

            //show/hide '+' zoom
            if(this.currentZoom >= this.maxZoom)
                this.showMaxZoom = false;
            else
                this.showMaxZoom = true;

            //show/hide '-' zoom
            if(this.currentZoom <= this.minZoom)
                this.showMinZoom = false;
            else
                this.showMinZoom = true;
        },
    }
};
</script>
